import { RepairPartFilter, RepairPartFilterFormValue } from '../models/repair-part-filter.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of, map, throwError } from 'rxjs';
import { DownloadRequest } from '../models/download-filter.model';
import { utils } from '../helper/utils';
import { RepairPartHeader } from '../models/repair-part.model';
import { RepairPartResponse } from '../models/repair-part-response.model';
import { CreateRepairPartRequest } from '../models/create-repair-part-request.model';
import { CodeDescription } from '../models/code-description.model';
import { AuditLog } from '../models/audit-log.model';

type UpdateRepairPartRequest = {
  maxQuantity: number;
};

@Injectable({
  providedIn: 'root',
})
export class RepairPartService {
  filterData: RepairPartFilter = {};
  constructor(private readonly httpClient: HttpClient) {}

  createRepairPart(request: CreateRepairPartRequest): Observable<RepairPartResponse> {
    const url = utils.GetMasterDataManagementUrl('repair-part');
    return this.httpClient.post<RepairPartResponse>(url, request);
  }

  updateRepairPart(
    repairMode: string,
    repairCode: string,
    partCode: string,
    request: UpdateRepairPartRequest,
  ): Observable<RepairPartResponse> {
    const path = `repair-part/${encodeURIComponent(repairMode)}/${encodeURIComponent(repairCode)}/${encodeURIComponent(partCode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.put<RepairPartResponse>(url, request);
  }

  private buildApiParams(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: RepairPartFilter,
  ): { [param: string]: string | number | boolean | string[] } {
    const params: { [param: string]: string | number | boolean | string[] } = {};
    params['page'] = page;
    params['limit'] = pageLimit > 0 ? pageLimit : 100;
    if (sort) {
      params['sort'] = sort;
    }
    if (!filters) {
      return params;
    }
    if (filters.repairModeList && Array.isArray(filters.repairModeList) && filters.repairModeList.length > 0) {
      params['repairModeList'] = filters.repairModeList.join(',');
    }
    if (filters.repairCodeList && Array.isArray(filters.repairCodeList) && filters.repairCodeList.length > 0) {
      params['repairCodeList'] = filters.repairCodeList.join(',');
    }
    if (filters.partCodeList && Array.isArray(filters.partCodeList) && filters.partCodeList.length > 0) {
      params['partCodeList'] = filters.partCodeList.join(',');
    }
    if (filters.maxQuantityOperator) {
      params['maxQuantityOperator'] = filters.maxQuantityOperator;
    }
    if (filters.maxQuantityValue !== undefined && filters.maxQuantityValue !== null) {
      params['maxQuantityValue'] = filters.maxQuantityValue;
    }
    if (filters.SearchText) {
      params['SearchText'] = filters.SearchText;
    }
    return params;
  }

  getRepairPartList(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: RepairPartFilter,
  ): Observable<HttpResponse<RepairPartHeader[]>> {
    const params = this.buildApiParams(page, pageLimit, sort, filters);

    const url = utils.GetMasterDataManagementUrl('repair-part');
    return this.httpClient.get<RepairPartHeader[]>(url, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<RepairPartHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
          }));
          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
      catchError(() => {
        return of(new HttpResponse<RepairPartHeader[]>({ body: [], headers: undefined }));
      }),
    );
  }

  getRepairPartByCompositeKey(repairMode: string, repairCode: string, partCode: string): Observable<RepairPartResponse | null> {
    const path = `repair-part/${encodeURIComponent(repairMode)}/${encodeURIComponent(repairCode)}/${encodeURIComponent(partCode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.get<RepairPartResponse>(url).pipe(catchError(() => of(null)));
  }

  deleteRepairPart(repairMode: string, repairCode: string, partCode: string): Observable<void> {
    const path = `repair-part/${encodeURIComponent(repairMode)}/${encodeURIComponent(repairCode)}/${encodeURIComponent(partCode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.delete<void>(url).pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * GET all distinct repair mode values for filter dropdowns
   */
  getAllModes(): Observable<string[]> {
    const url = utils.GetMasterDataManagementUrl('repair-part/all/modes');
    return this.httpClient.get<string[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * GET all distinct repair code values for filter typeahead
   */
  getAllCodes(): Observable<string[]> {
    const url = utils.GetMasterDataManagementUrl('repair-part/all/codes');
    return this.httpClient.get<string[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * GET all distinct part code values for filter typeahead
   */
  getAllParts(): Observable<string[]> {
    const url = utils.GetMasterDataManagementUrl('repair-part/all/parts');
    return this.httpClient.get<string[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * GET all repair modes with descriptions for create/edit dropdowns
   */
  getAllRepairModes(): Observable<CodeDescription[]> {
    const url = utils.GetMasterDataManagementUrl('repair-modes/mode-description/all');
    return this.httpClient.get<CodeDescription[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * GET all repair codes with descriptions for create/edit typeahead
   */
  getAllRepairCodes(repairMode?: string): Observable<CodeDescription[]> {
    let path = 'repair-codes/code-description/all';
    if (repairMode) {
      path += `?repairMode=${encodeURIComponent(repairMode)}`;
    }
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.get<CodeDescription[]>(url).pipe(catchError(() => of([])));
  }

  /**
   * GET the part options for the create repair part association typeahead.
   * Only active parts are returned, since an inactive part cannot be associated with a repair mode/code.
   */
  getAllPartsWithDescription(): Observable<CodeDescription[]> {
    const url = utils.GetMasterDataManagementUrl('parts/part-description/all');
    return this.httpClient.get<CodeDescription[]>(url, { params: { activeOnly: true } }).pipe(catchError(() => of([])));
  }

  /**
   * GET audit logs for a repair part association.
   * Endpoint: /api/repair-part/{repairMode}/{repairCode}/{partCode}/audit-logs
   */
  getRepairPartAuditLogs(
    repairMode: string,
    repairCode: string,
    partCode: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    const path = `repair-part/${encodeURIComponent(repairMode)}/${encodeURIComponent(repairCode)}/${encodeURIComponent(partCode)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient
      .get<AuditLog[]>(utils.GetMasterDataManagementUrl(path), { observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }))));
  }

  private repairPartFilterFormValue: RepairPartFilterFormValue | null = null;
  setRepairPartFilterFormValue(value: RepairPartFilterFormValue | null) {
    this.repairPartFilterFormValue = value;
  }
  getRepairPartFilterFormValue(): RepairPartFilterFormValue | null {
    return this.repairPartFilterFormValue;
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('repair-part');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }
}
